import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AppDispatch, RootState } from '../../../store/store';
import { confirmCheckout, clearSelectedEnrollment } from '../store/enrollmentsSlice';
import { formatMoney } from '../../../commodity/format/money';
import { formatDeadline, REQUEST_OUTCOME_COPY } from '../components/enrollmentRequest';
import { closingLabel } from '../vocabulary/learningVocabulary';

/**
 * Landing page Stripe redirects back to. In `success` mode it confirms the
 * Checkout session with the backend (the webhook is the backstop). Since
 * D-P07-03 (CTO, 12 Oct 2026) a completed checkout only AUTHORIZES the card:
 * the page then says the request was sent and is waiting for the teacher, and
 * that nothing has been charged. "Activada" appears only once the teacher
 * accepted and the server captured. In `cancel` mode it lets the parent try
 * again.
 */
const EnrollmentCheckoutReturn: React.FC<{ mode: 'success' | 'cancel' }> = ({ mode }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { selectedEnrollment, submitting, error } = useSelector(
    (state: RootState) => state.enrollments,
  );

  const sessionId = params.get('session_id') || '';
  const enrollmentId = params.get('enrollmentId') || '';
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (mode === 'success' && sessionId && enrollmentId) {
      dispatch(confirmCheckout({ enrollmentId, sessionId })).finally(() => setConfirmed(true));
    }
    return () => {
      dispatch(clearSelectedEnrollment());
    };
  }, [dispatch, mode, sessionId, enrollmentId]);

  if (mode === 'cancel') {
    return (
      <div style={containerStyle}>
        <div style={{ ...bannerStyle, background: '#fff3bf', border: '1px solid #ffd43b' }}>
          <h2 style={{ margin: 0 }}>Pago cancelado</h2>
          <p style={{ margin: '8px 0 0' }}>
            No se completo el pago. Tu inscripcion sigue pendiente; puedes intentarlo de nuevo.
          </p>
        </div>
        <button onClick={() => navigate('/courses')} style={primaryButtonStyle}>
          Volver a cursos
        </button>
      </div>
    );
  }

  const active = selectedEnrollment?.enrollment.state === 'Active';

  if (submitting || !confirmed) {
    return (
      <div style={containerStyle}>
        <p>Confirmando tu pago...</p>
      </div>
    );
  }

  // PRT-07-FR-09 / FR-11 — a checkout the server recorded as unpaid (expired,
  // or an asynchronous method that failed) will not activate on its own. The
  // "processing" copy below promised it would, so a family whose payment had
  // already failed was told to wait for an enrollment that was never coming.
  if (selectedEnrollment?.enrollment.state === 'PaymentFailed') {
    return (
      <div style={containerStyle}>
        <div style={{ ...bannerStyle, background: '#ffe3e3', border: '1px solid #ff8787' }}>
          <h2 style={{ margin: 0 }}>No se completó el pago</h2>
          <p style={{ margin: '8px 0 0' }}>
            Tu inscripción no se activó. Puedes intentarlo de nuevo desde el curso.
          </p>
        </div>
        <button onClick={() => navigate('/courses')} style={primaryButtonStyle}>
          Volver a cursos
        </button>
      </div>
    );
  }

  // PRT-07-FR-05 / FR-11 — authorized, not charged. Saying "activada" here
  // would tell the family they are enrolled and paid when neither is true yet.
  const pending = selectedEnrollment?.enrollment;
  if (pending?.state === 'PendingTeacherAcceptance') {
    return (
      <div style={containerStyle}>
        <div style={{ ...bannerStyle, background: '#e7f5ff', border: '1px solid #74c0fc' }}>
          <h2 style={{ margin: 0 }}>Solicitud enviada, esperando al profesor</h2>
          <p style={{ margin: '8px 0 0' }}>{REQUEST_OUTCOME_COPY.awaiting}</p>
          <p style={{ margin: '8px 0 0' }}>
            Monto autorizado (sin cobrar):{' '}
            <strong>{formatMoney(pending.total, pending.currency)}</strong>
          </p>
          <p style={{ margin: '8px 0 0' }}>
            El profesor tiene hasta el <strong>{formatDeadline(pending.acceptBy)}</strong> para
            responder.
          </p>
        </div>
        <button
          onClick={() => navigate(`/enrollments/${pending.enrollmentId}`)}
          style={primaryButtonStyle}
        >
          Ver solicitud
        </button>
      </div>
    );
  }

  if (pending?.state === 'TeacherDeclined' || pending?.state === 'AcceptanceExpired') {
    return (
      <div style={containerStyle}>
        <div style={{ ...bannerStyle, background: '#fff3bf', border: '1px solid #ffd43b' }}>
          <h2 style={{ margin: 0 }}>
            {pending.state === 'TeacherDeclined' ? 'Solicitud no aceptada' : 'Solicitud vencida'}
          </h2>
          <p style={{ margin: '8px 0 0' }}>
            {pending.state === 'TeacherDeclined'
              ? REQUEST_OUTCOME_COPY.declined
              : REQUEST_OUTCOME_COPY.expired}
          </p>
        </div>
        <button onClick={() => navigate('/courses')} style={primaryButtonStyle}>
          Volver a cursos
        </button>
      </div>
    );
  }

  if (!active) {
    return (
      <div style={containerStyle}>
        <div style={{ ...bannerStyle, background: '#fff3bf', border: '1px solid #ffd43b' }}>
          <h2 style={{ margin: 0 }}>Estamos confirmando tu pago</h2>
          <p style={{ margin: '8px 0 0' }}>
            {error
              ? error
              : 'Tu pago se esta procesando. Tu inscripcion se activara en unos momentos.'}
          </p>
        </div>
        {enrollmentId && (
          <button
            onClick={() => navigate(`/enrollments/${enrollmentId}`)}
            style={primaryButtonStyle}
          >
            Ver inscripcion
          </button>
        )}
      </div>
    );
  }

  const { enrollment, milestones } = selectedEnrollment!;
  return (
    <div style={containerStyle}>
      <div style={{ ...bannerStyle, background: '#d3f9d8', border: '1px solid #51cf66' }}>
        <h2 style={{ margin: 0 }}>Inscripcion activada</h2>
        <p style={{ margin: '8px 0 0' }}>
          ID de inscripcion: <code>{enrollment.enrollmentId}</code>
        </p>
        {enrollment.bookingFeeReleased && (
          <p style={{ margin: '8px 0 0', color: '#2b8a3e' }}>
            Booking fee de {formatMoney(enrollment.bookingFeeAmount, enrollment.currency)} marcado
            como liberable.
          </p>
        )}
      </div>

      <h3>8 Milestones</h3>
      <ol>
        {milestones.map((m) => (
          <li key={m.milestoneId} style={{ marginBottom: 4 }}>
            {m.type === 'final_project' ? closingLabel() : `Sesion ${m.sequence}`} —{' '}
            {formatMoney(m.amount, enrollment?.currency)}{' '}
            <span style={{ color: '#868e96' }}>({m.status})</span>
          </li>
        ))}
      </ol>

      <button onClick={() => navigate('/courses')} style={primaryButtonStyle}>
        Volver a cursos
      </button>
    </div>
  );
};

const containerStyle: React.CSSProperties = { maxWidth: 720, margin: '0 auto' };

const bannerStyle: React.CSSProperties = {
  borderRadius: 8,
  padding: 16,
  marginBottom: 24,
};

const primaryButtonStyle: React.CSSProperties = {
  width: '100%',
  padding: 14,
  backgroundColor: '#1971c2',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 16,
  fontWeight: 600,
  cursor: 'pointer',
};

export default EnrollmentCheckoutReturn;
